import { useState } from 'react'
import { cn } from '../../../utils/cn'

interface QuestionOption {
  id: string
  label: string
  value: number
}

interface AssessmentQuestionProps {
  questionNumber: number
  questionText: string
  options?: QuestionOption[]
  selectedValue?: string | undefined
  onSelect?: (value: string) => void
  className?: string
}

const defaultOptions: QuestionOption[] = [
  { id: 'strongly-agree', label: 'Strongly Agree', value: 5 },
  { id: 'agree', label: 'Agree', value: 4 },
  { id: 'neutral', label: 'Neutral', value: 3 },
  { id: 'disagree', label: 'Disagree', value: 2 },
  { id: 'strongly-disagree', label: 'Strongly Disagree', value: 1 },
]

export function AssessmentQuestion({
  questionNumber,
  questionText,
  options = defaultOptions,
  selectedValue,
  onSelect,
  className,
}: AssessmentQuestionProps) {
  const [selected, setSelected] = useState<string | undefined>(selectedValue)

  const handleSelect = (optionId: string) => {
    setSelected(optionId)
    onSelect?.(optionId)
  }

  return (
    <div
      className={cn(
        'rounded-[19px] px-[24px] md:px-[40px] py-[24px] md:py-[32px] bg-[#F5F6F8]',
        className
      )}
    >
      <div className='flex flex-col gap-[20px] max-w-[900px]'>
        {/* Question Header */}
        <div className='flex flex-col gap-[16px] text-start'>
          <p className='text-[16px] font-normal text-[#4e4d4d] leading-[24px]'>
            Question {questionNumber}
          </p>
          <h3 className='text-[16px] font-medium leading-[24px] text-[#151515]'>
            {questionText}
          </h3>
        </div>

        {/* Options */}
        <div className='flex flex-col gap-[12px]'>
          {options.map(option => {
            const isSelected = selected === option.id

            return (
              <button
                key={option.id}
                onClick={() => handleSelect(option.id)}
                className={cn(
                  'relative flex w-full items-center justify-between px-[24px] py-[8px] text-left transition-all',
                  isSelected
                    ? 'bg-white border-l-[6px] border-l-black rounded-r-[6px] shadow-[2px_10px_53px_-10px_rgba(16,24,40,0.09)]'
                    : 'border border-[#e0e0e0] rounded-[6px]'
                )}
              >
                <span
                  className={cn(
                    'text-[16px] font-medium leading-[24px]',
                    isSelected ? 'text-[#151515]' : 'text-[#4e4d4d]'
                  )}
                >
                  {option.label}
                </span>

                {/* Radio button */}
                <div
                  className={cn(
                    'flex h-[24px] w-[24px] items-center justify-center rounded-full border-2 transition-colors',
                    isSelected ? 'border-[#FF6600]' : 'border-[#4e4d4d]'
                  )}
                >
                  {isSelected && (
                    <div className='h-[8px] w-[8px] rounded-full bg-[#FF6600]' />
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
